import { NavLink, Outlet, useNavigate, useLocation, Link } from 'react-router-dom';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '../lib/supabase';
import { t } from '../i18n/nl.js';
import { useActionsCount } from '../contexts/ActionsCountContext';
import { fetchOpenActiesCount } from '../utils/actiesUtils';
import { getActiveClientMemberships, primaryMembershipCredits } from '../utils/membershipUtils';

const COACH_NAV = [
  { to: '/coach/rooster',       label: t('nav.rooster'),          icon: '📅' },
  { to: '/coach/clients',       label: t('nav.clients'),          icon: '👥' },
  { to: '/coach/aanvragen',     label: t('nav.ptAanvragen'),      icon: '📩', badge: 'ptAanvragen' },
  { to: '/coach/acties',        label: t('nav.acties'),           icon: '⚡', adminOnly: true },
  { to: '/coach/facturen',      label: t('nav.facturering'),      icon: '🧾' },
  { to: '/coach/opzeggingen',   label: t('nav.opzeggingen'),      icon: '📋' },
  { to: '/coach/recepten',      label: t('nav.recepten'),         icon: '🥗' },
  { to: '/coach/push',          label: t('nav.pushNotificaties'), icon: '🔔' },
  { to: '/admin/gebruikers',    label: t('nav.gebruikersBeheer'), icon: '🔑', adminOnly: true },
  { to: '/admin/cron-dashboard',label: t('nav.cronDashboard'),    icon: '🕐', adminOnly: true },
  { to: '/instellingen',        label: t('nav.instellingen'),     icon: '⚙️' },
];

const CLIENT_NAV = [
  { to: '/client/rooster',      label: t('nav.rooster'),        icon: '📅' },
  { to: '/client/voeding',      label: t('nav.voeding'),        icon: '🥗' },
  { to: '/client/lessen',       label: t('nav.mijnLessen'),     icon: '🏃' },
  { to: '/client/afspraken',    label: t('nav.mijnAfspraken'),  icon: '📋' },
  { to: '/client/facturen',     label: t('nav.mijnFacturen'),   icon: '🧾' },
  { to: '/client/credits',      label: t('nav.mijnCredits'),    icon: '💳' },
  { to: '/mijn-abonnement',     label: t('nav.mijnAbonnement'), icon: '📋' },
  { to: '/client/instellingen', label: t('nav.mijnGegevens'),   icon: '👤' },
];

const ROLE_LABELS = {
  superadmin:  t('nav.roles.superadmin'),
  admin_coach: t('nav.roles.admin_coach'),
  coach:       t('nav.roles.coach'),
  client:      t('nav.roles.client'),
};

export default function Layout() {
  const { user, role, signOut, isClientRole } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { count: openActiesCount, setCount: setOpenActiesCount } = useActionsCount();
  const [clientCredits, setClientCredits] = useState(null);
  const [clientKlantnummer, setClientKlantnummer] = useState(null);
  const [aanmaningBanner, setAanmaningBanner] = useState(null); // { due_date }
  const [pendingAgreementToken, setPendingAgreementToken] = useState(null); // token string
  const [ptAanvraagCount, setPtAanvraagCount] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    setSidebarOpen(false);
  }, [location.pathname]);

  const isAdminRoleForBadge = ['superadmin', 'admin_coach'].includes(role);

  useEffect(() => {
    if (!user?.id || isClientRole) return;
    if (!isAdminRoleForBadge) return;
    fetchOpenActiesCount(user.id).then(count => setOpenActiesCount(count));
  }, [user?.id, isClientRole, isAdminRoleForBadge, location.pathname]);

  useEffect(() => {
    if (!user?.id || isClientRole) return;
    supabase
      .from('appointment_requests')
      .select('id', { count: 'exact', head: true })
      .eq('coach_id', user.id)
      .in('status', ['pending', 'rescheduled'])
      .then(({ count }) => setPtAanvraagCount(count ?? 0));
  }, [user?.id, isClientRole, location.pathname]);

  useEffect(() => {
    if (!isClientRole || !user?.id) return;
    getActiveClientMemberships(user.id)
      .then(memberships => {
        setClientCredits(memberships.length
          ? memberships.reduce((sum, m) => sum + primaryMembershipCredits(m), 0)
          : null);
      })
      .catch(() => setClientCredits(null));

    supabase
      .from('profiles')
      .select('customer_number')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => setClientKlantnummer(data?.customer_number ?? null));

    // Check openstaande aanmaningen
    supabase
      .from('invoices')
      .select('id, invoice_reminders(new_due_date, type)')
      .eq('client_id', user.id)
      .in('status', ['final_notice', 'collections'])
      .limit(1)
      .maybeSingle()
      .then(({ data }) => {
        if (!data) { setAanmaningBanner(null); return; }
        const reminders = data.invoice_reminders || [];
        const latest = reminders
          .filter(r => r.new_due_date)
          .sort((a, b) => new Date(b.new_due_date) - new Date(a.new_due_date))[0];
        setAanmaningBanner({ due_date: latest?.new_due_date || null });
      });

    // UITBREIDING 11: Check openstaand agreement_token
    supabase
      .from('agreement_tokens')
      .select('token')
      .eq('client_id', user.id)
      .is('used_at', null)
      .is('rejected_at', null)
      .gt('expires_at', new Date().toISOString())
      .order('created_at', { ascending: false })
      .order('id', { ascending: false })
      .limit(1)
      .maybeSingle()
      .then(({ data }) => setPendingAgreementToken(data?.token || null));
  }, [isClientRole, user?.id, location.pathname]);

  async function handleSignOut() {
    await signOut();
    navigate('/login');
  }

  const roleLabel = ROLE_LABELS[role] ?? role;
  const isAdminRole = ['superadmin', 'admin_coach'].includes(role);
  const navItems = isClientRole
    ? CLIENT_NAV
    : COACH_NAV.filter(item => !item.adminOnly || isAdminRole);

  const portalLabel = isClientRole ? t('nav.clientPortal') : t('nav.coachPortal');

  return (
    <div className="app-shell">
      {/* Mobiele topbar met hamburger-knop */}
      <div className="mobile-topbar">
        <button
          className="sidebar-toggle"
          onClick={() => setSidebarOpen(o => !o)}
          aria-label={t('nav.menuOpenen')}
        >
          ☰
        </button>
        <div style={{ fontWeight: 700, fontSize: 15, color: '#fff' }}>
          {t('app.name')}
        </div>
      </div>

      {/* Backdrop achter uitgeklapte sidebar (mobiel) */}
      <div
        className={`sidebar-backdrop${sidebarOpen ? ' is-open' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`app-sidebar${sidebarOpen ? ' is-open' : ''}`}>
        {/* Logo */}
        <div style={{ padding: '24px 20px 20px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontWeight: 700, fontSize: 16, color: '#fff', letterSpacing: '-0.3px' }}>
            {t('app.name')}
          </div>
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', marginTop: 2 }}>
            {portalLabel}
          </div>
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: '12px 8px' }}>
          {navItems.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              style={({ isActive }) => ({
                display: 'flex',
                alignItems: 'center',
                gap: 10,
                padding: '9px 12px',
                borderRadius: 'var(--radius)',
                color: isActive ? '#fff' : 'var(--color-sidebar-text)',
                background: isActive ? 'var(--color-sidebar-active)' : 'transparent',
                fontWeight: isActive ? 600 : 400,
                fontSize: 14,
                marginBottom: 2,
                transition: 'background 0.15s',
              })}
              onMouseEnter={e => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.background = 'var(--color-sidebar-hover)';
                }
              }}
              onMouseLeave={e => {
                if (!e.currentTarget.classList.contains('active')) {
                  e.currentTarget.style.background = 'transparent';
                }
              }}
            >
              <span>{item.icon}</span>
              <span style={{ flex: 1 }}>{item.label}</span>
              {item.to === '/coach/acties' && openActiesCount > 0 && (
                <span style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 99,
                  minWidth: 18,
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {openActiesCount}
                </span>
              )}
              {item.badge === 'ptAanvragen' && ptAanvraagCount > 0 && (
                <span style={{
                  background: '#dc2626',
                  color: '#fff',
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '1px 6px',
                  borderRadius: 99,
                  minWidth: 18,
                  textAlign: 'center',
                  flexShrink: 0,
                }}>
                  {ptAanvraagCount}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* User info + logout */}
        <div style={{ padding: '16px 12px', borderTop: '1px solid rgba(255,255,255,0.1)' }}>
          <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.55)', marginBottom: 2 }}>
            {roleLabel}
          </div>
          <div style={{ fontSize: 13, color: '#fff', fontWeight: 500, marginBottom: isClientRole ? 4 : 12, wordBreak: 'break-all' }}>
            {user?.email}
          </div>
          {isClientRole && clientKlantnummer && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', marginBottom: 4 }}>
              {t('nav.klantnummer')} {clientKlantnummer}
            </div>
          )}
          {isClientRole && clientCredits !== null && (
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.75)', marginBottom: 12, fontWeight: 600 }}>
              {clientCredits} {t('nav.creditsResterend')}
            </div>
          )}
          <button
            onClick={handleSignOut}
            className="btn btn-ghost btn-sm"
            style={{ width: '100%', color: 'rgba(255,255,255,0.7)', borderColor: 'rgba(255,255,255,0.2)' }}
          >
            {t('nav.uitloggen')}
          </button>
        </div>
      </aside>

      {/* Main content */}
      <main className="app-main">
        {/* Aanmaningsbanner voor cliënten */}
        {isClientRole && aanmaningBanner && (
          <div style={{
            background: '#dc2626',
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              ⚠ {t('banner.openstaandeAanmaning')}
              {aanmaningBanner.due_date && (
                <> Betaal voor <strong>{aanmaningBanner.due_date}</strong> om incassokosten te voorkomen.</>
              )}
            </span>
            <Link
              to="/client/facturen"
              style={{ color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'underline', flexShrink: 0 }}
            >
              {t('banner.bekijkFacturen')}
            </Link>
          </div>
        )}

        {/* UITBREIDING 11: Overeenkomstbanner */}
        {isClientRole && pendingAgreementToken && (
          <div style={{
            background: '#1d4ed8',
            color: '#fff',
            padding: '12px 20px',
            display: 'flex',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 12,
            flexShrink: 0,
          }}>
            <span style={{ fontWeight: 600, fontSize: 14 }}>
              📋 {t('banner.openstaandeOvereenkomst')}
            </span>
            <Link
              to={`/overeenkomst/${pendingAgreementToken}`}
              style={{ color: '#fff', fontWeight: 700, fontSize: 13, textDecoration: 'underline', flexShrink: 0 }}
            >
              {t('banner.bekijkOvereenkomst')} →
            </Link>
          </div>
        )}
        <div className="app-content">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
